import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useVitals from '../hooks/useVitals';

const { FiHeart, FiThermometer, FiActivity, FiTrendingUp, FiWifi, FiWifiOff } = FiIcons;

const VitalsMonitor = ({ patient, thresholds }) => {
  const activeThresholds = thresholds || {};

  // Wire to live socket events.
  // Uses patient.vitals as the initial fallback so existing static data
  // is shown immediately while waiting for the first live event.
  const { vitals: liveVitals, isLive, lastUpdated } = useVitals(
    patient?.id,
    patient?.vitals ?? {}
  );

  // Merge: live socket data takes precedence over patient.vitals prop
  const vitals = {
    hr:      liveVitals?.hr      ?? patient?.vitals?.hr      ?? '--',
    bp:      liveVitals?.bp      ?? patient?.vitals?.bp      ?? '--/--',
    temp:    liveVitals?.temp    ?? patient?.vitals?.temp    ?? '--',
    glucose: liveVitals?.glucose ?? patient?.vitals?.glucose ?? '--',
  };

  const getVitalStatus = (type, value) => {
    if (value === undefined || value === null || value === '--') return 'normal';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'normal';
    let thresh;
    switch (type) {
      case 'hr':      thresh = activeThresholds.heartRate;   break;
      case 'temp':    thresh = activeThresholds.temperature; break;
      case 'glucose': thresh = activeThresholds.glucose;     break;
      default: return 'normal';
    }
    if (!thresh) return 'normal';
    if (thresh.max !== undefined && numValue >= thresh.max)         return 'high';
    if (thresh.max !== undefined && numValue >= (thresh.max * 0.9)) return 'elevated';
    if (thresh.min !== undefined && numValue < thresh.min)          return 'low';
    return 'normal';
  };

  const getBpStatus = (bpString) => {
    if (!bpString || typeof bpString !== 'string' || bpString === '--/--') return 'normal';
    const parts = bpString.split('/');
    if (parts.length !== 2) return 'normal';
    const sys = parseFloat(parts[0]);
    const dia = parseFloat(parts[1]);
    const thresh = activeThresholds.bloodPressure;
    if (!thresh) return 'normal';
    let sysStatus = 'normal';
    if (thresh.sys) {
      if (sys >= thresh.sys)          sysStatus = 'high';
      else if (sys >= thresh.sys * 0.9) sysStatus = 'elevated';
      else if (sys < 90)              sysStatus = 'low';
    }
    let diaStatus = 'normal';
    if (thresh.dia) {
      if (dia >= thresh.dia)          diaStatus = 'high';
      else if (dia >= thresh.dia * 0.9) diaStatus = 'elevated';
      else if (dia < 60)              diaStatus = 'low';
    }
    if (sysStatus === 'high'     || diaStatus === 'high')     return 'high';
    if (sysStatus === 'elevated' || diaStatus === 'elevated') return 'elevated';
    if (sysStatus === 'low'      || diaStatus === 'low')      return 'low';
    return 'normal';
  };

  const vitalsData = [
    { time: '00:00', heartRate: 72,  bloodPressureSys: 120, bloodPressureDia: 80, temperature: 98.6 },
    { time: '04:00', heartRate: 68,  bloodPressureSys: 118, bloodPressureDia: 78, temperature: 98.4 },
    { time: '08:00', heartRate: 75,  bloodPressureSys: 125, bloodPressureDia: 82, temperature: 98.7 },
    { time: '12:00', heartRate: 82,  bloodPressureSys: 135, bloodPressureDia: 88, temperature: 99.1 },
    { time: '16:00', heartRate: 95,  bloodPressureSys: 145, bloodPressureDia: 95, temperature: 98.8 },
    { time: '20:00', heartRate: 88,  bloodPressureSys: 140, bloodPressureDia: 90, temperature: 98.6 },
  ];

  const currentVitals = useMemo(() => {
    const hrThresh      = activeThresholds.heartRate      || {};
    const bpThresh      = activeThresholds.bloodPressure  || {};
    const tempThresh    = activeThresholds.temperature    || {};
    const glucoseThresh = activeThresholds.glucose        || {};
    return [
      { id: 'hr',      label: 'Heart Rate',      value: vitals.hr?.toString()   || '--',    unit: 'bpm',   status: getVitalStatus('hr', vitals.hr),      icon: FiHeart,       range: hrThresh.min      ? `${hrThresh.min}-${hrThresh.max} bpm`              : 'N/A' },
      { id: 'bp',      label: 'Blood Pressure',  value: vitals.bp               || '--/--', unit: 'mmHg',  status: getBpStatus(vitals.bp),               icon: FiActivity,    range: bpThresh.sys      ? `<${bpThresh.sys}/${bpThresh.dia} mmHg`            : 'N/A' },
      { id: 'temp',    label: 'Temperature',     value: vitals.temp?.toString() || '--',    unit: '°F',    status: getVitalStatus('temp', vitals.temp),   icon: FiThermometer, range: tempThresh.min    ? `${tempThresh.min}-${tempThresh.max}°F`            : 'N/A' },
      { id: 'glucose', label: 'Glucose',         value: vitals.glucose?.toString() || '--', unit: 'mg/dL', status: getVitalStatus('glucose', vitals.glucose), icon: FiTrendingUp, range: glucoseThresh.min ? `${glucoseThresh.min}-${glucoseThresh.max} mg/dL` : 'N/A' },
    ];
  }, [vitals, activeThresholds]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'high':     return 'text-red-700 bg-red-100 border-red-200 ring-1 ring-red-300';
      case 'elevated': return 'text-orange-700 bg-orange-100 border-orange-200';
      case 'low':      return 'text-blue-700 bg-blue-100 border-blue-200';
      case 'normal':   return 'text-green-700 bg-green-100 border-green-200';
      default:         return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  if (!patient) return null;

  const hasHighRisk = currentVitals.some(v => v.status === 'high');

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Real-time Vitals Monitor</h2>
          <p className="text-sm text-gray-600">Continuous monitoring and trend analysis</p>
        </div>
        {/* Live status indicator */}
        <div className="flex items-center gap-1.5">
          <SafeIcon
            icon={isLive ? FiWifi : FiWifiOff}
            className={`text-sm ${isLive ? 'text-emerald-500' : 'text-gray-400'}`}
          />
          <span className={`text-xs font-semibold ${isLive ? 'text-emerald-600' : 'text-gray-400'}`}>
            {isLive
              ? `Live · ${lastUpdated?.toLocaleTimeString()}`
              : 'Awaiting signal'}
          </span>
        </div>
      </div>

      <div className="p-6">
        {/* Current Vitals Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {currentVitals.map((vital, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className={`p-4 rounded-lg border-2 ${getStatusColor(vital.status)} transition-all duration-300`}
            >
              <div className="flex items-center justify-between mb-2">
                <SafeIcon icon={vital.icon} className="text-lg" />
                <span className="px-2 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-white/50 backdrop-blur-sm">
                  {vital.status}
                </span>
              </div>
              <div className="mb-1">
                <span className="text-2xl font-bold">{vital.value}</span>
                <span className="text-sm opacity-80 ml-1">{vital.unit}</span>
              </div>
              <div className="text-xs font-medium opacity-90">{vital.label}</div>
              <div className="text-xs opacity-75 mt-1">Ref: {vital.range}</div>
            </motion.div>
          ))}
        </div>

        {/* Vitals Chart — static for now, will bind to telemetry_events history later */}
        <div className="mb-6">
          <h3 className="text-md font-medium text-gray-900 mb-4">24-Hour Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={vitalsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="heartRate"       stroke="#3b82f6" strokeWidth={2} name="Heart Rate (bpm)"   />
                <Line type="monotone" dataKey="bloodPressureSys" stroke="#ef4444" strokeWidth={2} name="Systolic BP (mmHg)" />
                <Line type="monotone" dataKey="temperature"     stroke="#f59e0b" strokeWidth={2} name="Temperature (°F)"   />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Risk Assessment Alert */}
        {hasHighRisk && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-red-50 border border-red-200 rounded-lg p-4"
          >
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiActivity} className="text-red-600 text-xl mt-1" />
              <div>
                <h4 className="font-medium text-red-900 mb-1">Elevated Risk Detected</h4>
                <p className="text-sm text-red-700 mb-3">
                  Current readings exceed critical thresholds. Immediate clinical attention recommended.
                </p>
                <div className="flex space-x-3">
                  <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">
                    Alert Physician
                  </button>
                  <button className="px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors">
                    View Assessment
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default VitalsMonitor;